/**
 * @fileoverview TipTap `signatureBlock` node — an atom block that stores a
 * SignatureSpec (JSON in a data attribute) and renders the user's
 * e-signature via a React NodeView. Round-trips through the canvas `html`
 * field; the PDF/Word exports convert the placeholder div to static markup
 * via `signatureToHtml` (MOR-931 follow-up: canvas e-signatures).
 *
 * An *unresolved* spec (no image, no text — e.g. agent-authored
 * `<div data-morgan-signature='{}'>`) renders as a placeholder;
 * `CanvasPanel` fills it with the current user's saved signature.
 *
 * The NodeView carries its own editing chrome (visible on hover/selection,
 * never exported): a drag handle, move up/down, resize and delete. An atom
 * node is otherwise inert in the document — without a `[data-drag-handle]`
 * element ProseMirror won't drag it, and users had no way to reposition or
 * remove a signature once it was inserted.
 */
import { Node, mergeAttributes } from "@tiptap/core";
import type { ReactNode } from "react";
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from "@tiptap/react";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Minus,
  Plus,
  X,
} from "lucide-react";
import { NodeSelection } from "@tiptap/pm/state";
import type { Editor } from "@tiptap/core";
import {
  encodeSignatureAttr,
  isResolvedSignatureSpec,
  signatureSpecFromAttr,
  SIG_MAX_HEIGHT_PX,
  SIG_MAX_WIDTH_PX,
  type SignatureSpec,
} from "./signatureHtml";

/** How much one resize step grows or shrinks the signature. */
const RESIZE_STEP = 1.2;
/** Keep the signature legible at the small end and sane at the large end. */
const MIN_SIG_HEIGHT_PX = 20;
const MAX_SIG_HEIGHT_PX = 200;

/**
 * Move the block one position up (-1) or down (1) among its siblings.
 * ProseMirror has no "move node" primitive, so cut the node out and
 * re-insert it on the other side of its neighbour, keeping it selected.
 */
function moveSignatureBlock(
  editor: Editor,
  pos: number | undefined,
  direction: -1 | 1,
): void {
  if (typeof pos !== "number") return;
  const { state, dispatch } = editor.view;
  const node = state.doc.nodeAt(pos);
  if (!node) return;
  const $pos = state.doc.resolve(pos);
  const parent = $pos.parent;
  const index = $pos.index();
  const neighbourIndex = index + direction;
  if (neighbourIndex < 0 || neighbourIndex >= parent.childCount) return;
  const neighbour = parent.child(neighbourIndex);
  const insertPos =
    direction === -1
      ? pos - neighbour.nodeSize
      : pos + node.nodeSize + neighbour.nodeSize;

  const tr = state.tr.delete(pos, pos + node.nodeSize);
  const mappedInsertPos = tr.mapping.map(insertPos);
  tr.insert(mappedInsertPos, node);
  tr.setSelection(NodeSelection.create(tr.doc, mappedInsertPos));
  dispatch(tr.scrollIntoView());
  editor.view.focus();
}

/** Scale the baked image dimensions, clamped to a sensible range. */
function resizedSpec(spec: SignatureSpec, factor: number): SignatureSpec {
  const height = spec.heightPx ?? SIG_MAX_HEIGHT_PX;
  const width = spec.widthPx ?? SIG_MAX_WIDTH_PX;
  const nextHeight = Math.round(height * factor);
  if (nextHeight < MIN_SIG_HEIGHT_PX || nextHeight > MAX_SIG_HEIGHT_PX) {
    return spec;
  }
  return {
    ...spec,
    heightPx: nextHeight,
    widthPx: Math.max(1, Math.round(width * factor)),
  };
}

function ControlButton({
  label,
  onClick,
  children,
  dragHandle,
}: {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  dragHandle?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      contentEditable={false}
      {...(dragHandle ? { "data-drag-handle": "" } : {})}
      onClick={onClick}
      className={`flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground ${
        dragHandle ? "cursor-grab active:cursor-grabbing" : ""
      }`}
    >
      {children}
    </button>
  );
}

function SignatureNodeView({
  node,
  editor,
  getPos,
  selected,
  updateAttributes,
  deleteNode,
}: ReactNodeViewProps) {
  const spec = (node.attrs.spec as SignatureSpec | null) ?? {};
  const resolved = isResolvedSignatureSpec(spec);

  const controls = (
    <div
      contentEditable={false}
      className={`absolute -top-3 right-0 z-10 flex items-center gap-0.5 rounded-md border border-border bg-background p-0.5 shadow-sm transition-opacity ${
        selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
      }`}
    >
      <ControlButton label="Drag to move the signature" dragHandle>
        <GripVertical className="h-3.5 w-3.5" />
      </ControlButton>
      <ControlButton
        label="Move signature up"
        onClick={() => moveSignatureBlock(editor, getPos(), -1)}
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </ControlButton>
      <ControlButton
        label="Move signature down"
        onClick={() => moveSignatureBlock(editor, getPos(), 1)}
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </ControlButton>
      {resolved && spec.image && (
        <>
          <ControlButton
            label="Make signature smaller"
            onClick={() =>
              updateAttributes({ spec: resizedSpec(spec, 1 / RESIZE_STEP) })
            }
          >
            <Minus className="h-3.5 w-3.5" />
          </ControlButton>
          <ControlButton
            label="Make signature larger"
            onClick={() =>
              updateAttributes({ spec: resizedSpec(spec, RESIZE_STEP) })
            }
          >
            <Plus className="h-3.5 w-3.5" />
          </ControlButton>
        </>
      )}
      <ControlButton label="Delete signature" onClick={() => deleteNode()}>
        <X className="h-3.5 w-3.5" />
      </ControlButton>
    </div>
  );

  return (
    <NodeViewWrapper
      className={`group relative my-3 rounded-md ring-offset-2 ring-offset-background ${
        selected ? "ring-2 ring-primary/50" : "hover:ring-1 hover:ring-border"
      }`}
    >
      {controls}
      {!resolved ? (
        <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
          Signature placeholder — your saved signature will appear here. If
          nothing shows, add a signature under Settings → Signature.
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          {spec.image ? (
            <img
              src={spec.image}
              alt="Signature"
              draggable={false}
              style={{
                width: spec.widthPx ? `${spec.widthPx}px` : undefined,
                height: spec.heightPx ? `${spec.heightPx}px` : undefined,
                maxWidth: spec.widthPx ? undefined : `${SIG_MAX_WIDTH_PX}px`,
                maxHeight: spec.heightPx ? undefined : `${SIG_MAX_HEIGHT_PX}px`,
                objectFit: "contain",
                alignSelf: "flex-start",
              }}
            />
          ) : (
            <span
              className="text-2xl"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              {spec.text}
            </span>
          )}
          {spec.name && (
            <span className="text-xs text-muted-foreground">{spec.name}</span>
          )}
        </div>
      )}
    </NodeViewWrapper>
  );
}

export const SignatureNode = Node.create({
  name: "signatureBlock",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      spec: {
        default: null,
        parseHTML: (el: HTMLElement) =>
          signatureSpecFromAttr(el.getAttribute("data-morgan-signature") ?? ""),
        renderHTML: (attrs: { spec: SignatureSpec | null }) => ({
          "data-morgan-signature": encodeSignatureAttr(attrs.spec ?? {}),
        }),
      },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-morgan-signature]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(SignatureNodeView);
  },
});
